import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { API_CLIENT_CONFIG } from '../core/api-client-config';
import {
  EconomicOfferScriptMetadata,
  TenderEconomicOfferCriteria,
  UpsertTenderEconomicOfferCriteriaRequest,
  ValidateEconomicOfferScriptRequest,
  ValidateEconomicOfferScriptResponse,
} from '../models';

@Injectable({ providedIn: 'root' })
export class TenderEconomicOfferCriteriaApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(API_CLIENT_CONFIG).apiBaseUrl}/api/admin/BackofficeTenderEconomicOfferCriteria`;

  private scriptMetadata$?: Observable<EconomicOfferScriptMetadata>;

  /**
   * Reads the criteria for a tender. Omit `id` for the latest revision, or pass
   * a revision's `id` to load that specific revision. Returns `null` when none
   * is configured yet.
   */
  getCriteria(tenderId: string, id?: string) {
    let params = new HttpParams().append('TenderId', tenderId);
    if (id) {
      params = params.append('Id', id);
    }
    return this.http.get<TenderEconomicOfferCriteria | null>(this.baseUrl, {
      params,
    });
  }

  /** Upserts the criteria. Publishing creates a new revision server-side. */
  upsertCriteria(request: UpsertTenderEconomicOfferCriteriaRequest) {
    return this.http.put<{ id: string }>(this.baseUrl, request);
  }

  /**
   * Validates (and optionally test-runs) an evaluation script. Omit
   * `testAnswer` for a parse-only syntax check.
   */
  validateScript(request: ValidateEconomicOfferScriptRequest) {
    return this.http.post<ValidateEconomicOfferScriptResponse>(
      `${this.baseUrl}/validate-script`,
      request,
    );
  }

  /**
   * Static presets + injected-variable reference for the script editor.
   * Fetched once and cached for the lifetime of the app.
   */
  getScriptMetadata() {
    if (!this.scriptMetadata$) {
      this.scriptMetadata$ = this.http
        .get<EconomicOfferScriptMetadata>(`${this.baseUrl}/script-metadata`)
        .pipe(shareReplay(1));
    }
    return this.scriptMetadata$;
  }
}
